import React, { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  writeBatch
} from "firebase/firestore";
import { db, auth } from "../firebase";
import { fetchOnlineCollectionMap, formatDateToDDMMYYYY, updateOrderSyncStatus, fetchParties, fetchPreviousDayLedger, cascadeLedgerBalance } from "../services/cashierService";
import * as XLSX from "xlsx";

import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";

const fmt = (v) => Number(v || 0).toFixed(2);

const getISTDate = () => {
  const now = new Date();
  const ist = new Date(now.getTime() + 5.5 * 60 * 60 * 1000);
  return ist.toISOString().slice(0, 10);
};

const styles = {
  container: {
    maxWidth: "97%",
    margin: "0 auto",
    padding: 16,
    fontFamily: "Arial, sans-serif"
  },
  header: {
    fontSize: 26,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 20
  },
  tabRow: {
    display: "flex",
    gap: 12,
    marginBottom: 20
  },
  tabBtn: (active) => ({
    flex: 1,
    padding: "12px 0",
    fontSize: 16,
    fontWeight: "bold",
    borderRadius: 8,
    border: "1px solid #999",
    background: active ? "#1976d2" : "#eee",
    color: active ? "#fff" : "#000",
    cursor: "pointer",
    transition: "all 0.2s"
  }),
  actionBtn: {
    padding: "8px 16px",
    fontSize: 14,
    borderRadius: 4,
    border: "none",
    cursor: "pointer",
    fontWeight: "bold"
  }
};

export default function CashierApproval() {
  const navigate = useNavigate();
  
  const [notSubmitted, setNotSubmitted] = useState([]);
  const [submitted, setSubmitted] = useState([]);
  const [approved, setApproved] = useState([]);
  const [tab, setTab] = useState("PENDING"); // PENDING | SUBMITTED | APPROVED | CASH_SUMMARY
  const [exportCountdown, setExportCountdown] = useState(15);

  const [searchTerm, setSearchTerm] = useState("");
  const [editingRow, setEditingRow] = useState(null);
  const [editDenoms, setEditDenoms] = useState({});
  const [editRd, setEditRd] = useState(0);
  const [editD, setEditD] = useState(0);
  const [loading, setLoading] = useState(true);
  const todayIST = getISTDate();
  const [exportDate, setExportDate] = useState(todayIST);
  const [partiesMap, setPartiesMap] = useState({});
  const [userRole, setUserRole] = useState(null);

  // 15-second export countdown timer whenever exportDate changes
  useEffect(() => {
    setExportCountdown(15);
    const interval = setInterval(() => {
      setExportCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [exportDate]);

  // Time Settings
  const [startTime, setStartTime] = useState("09:45");
  const [endTime, setEndTime] = useState("13:30");
  const [savingSettings, setSavingSettings] = useState(false);
  const [onlineCollectionMap, setOnlineCollectionMap] = useState({});
  const [cdmMap, setCdmMap] = useState({});
  const [pendingOrdersMap, setPendingOrdersMap] = useState({});
  const [DENOM_ORDER, setDenomOrder] = useState([500, 200, 100, 50, 20, 10, 5, 1]); // Fallback


  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate("/");
        return;
      }
      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        setUserRole(userSnap.data().role);
      }

      // Fetch Dynamic Denominations
      try {
        const cSnap = await getDoc(doc(db, "settings", "currencyConfig"));
        if (cSnap.exists() && cSnap.data().denominations) {
          setDenomOrder(cSnap.data().denominations.sort((a, b) => b - a));
        }
      } catch (err) {
        console.error("Error loading denominations:", err);
      }
    });
    return () => unsub();
  }, [navigate]);

  useEffect(() => {
    const loadParties = async () => {
      const list = await fetchParties("all"); // Already filters for hawker
      const m = {};
      list.forEach(h => { m[h.partyId] = h; });
      setPartiesMap(m);
    };
    loadParties();

    // Load Time Settings
    const loadSettings = async () => {
      const ref = doc(db, "settings", "cashierConfig");
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const d = snap.data();
        setStartTime(d.startTime || "09:45");
        setEndTime(d.endTime || "13:30");
      }
    };
    loadSettings();
  }, []);

  /* ===== LOAD DATA ===== */
  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const q = query(
          collection(db, "salesman_ledger"),
          where("dateIST", "==", exportDate)
        );
        const snap = await getDocs(q);
        const ledgerRows = snap.docs.map(d => ({ id: d.id, ...d.data() }));

        const hawkerList = await fetchParties("all");
        const fullRows = await Promise.all(hawkerList.map(async (h) => {
          const partyId = (h.partyId || h.id || "").trim();
          const existing = ledgerRows.find(lr => (lr.partyId || "").trim() === partyId);
          
          if (existing) return existing;

          // Missing record: Pre-calculating Opening Balance for display
          const prev = await fetchPreviousDayLedger(partyId, exportDate);
          return {
            id: `MOCK_${partyId}_${exportDate}`,
            partyId,
            dateIST: exportDate,
            openingBalance: prev?.closingBalance || 0,
            orderAmount: 0,
            cdmAmount: 0,
            nicheBrandAmount: 0,
            onlineCollected: 0,
            cashCollected: 0,
            closingBalance: prev?.closingBalance || 0,
            status: "PENDING",
            isMock: true
          };
        }));

        setNotSubmitted(fullRows.filter(r => r.status === "PENDING" || r.status === "REJECTED"));
        setSubmitted(fullRows.filter(r => r.status === "SUBMITTED"));
        setApproved(fullRows.filter(r => r.status === "APPROVED"));
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    }

    async function loadOnlineAndCDMData() {
        try {
            // Online data
            const data = await fetchOnlineCollectionMap(exportDate);
            setOnlineCollectionMap(data || {});

            // CDM data for estimates (hawker_cdm uses dd-mm-yyyy)
            const formattedDate = formatDateToDDMMYYYY(exportDate);
            const cdmSnap = await getDocs(
                query(collection(db, "hawker_cdm"), where("date", "==", formattedDate))
            );
            const cm = {};
            cdmSnap.forEach(d => {
                const row = d.data();
                cm[row.partyId] = {
                    cdm: Number(row.cdm || 0),
                    niche: Number(row.nicheBrands || 0)
                };
            });
            setCdmMap(cm);

        } catch (err) {
            console.error("Error loading online/cdm data:", err);
        }
    }

    async function loadPendingOrders() {
      try {
        const q = query(
          collection(db, "orders"),
          where("status", "==", "APPROVED"),
          where("ledgerSynced", "==", false)
        );
        const snap = await getDocs(q);
        const pm = {};
        snap.forEach(d => {
          const data = d.data();
          const pid = (data.partyId || "").trim();
          if (!pm[pid]) pm[pid] = [];
          pm[pid].push({ id: d.id, ...data });
        });
        setPendingOrdersMap(pm);
      } catch (err) {
        console.error("Error loading pending orders:", err);
      }
    }

    loadData();
    loadOnlineAndCDMData();
    loadPendingOrders();
  }, [exportDate]);


  // Combined filter and sorting logic
  const matchSearch = (p) => p.partyId.toLowerCase().includes(searchTerm.toLowerCase());
  const sortRowsByPartyType = (rows) => {
    return [...rows].sort((a, b) => {
      const typeA = partiesMap[a.partyId]?.partytype || "";
      const typeB = partiesMap[b.partyId]?.partytype || "";
      const typeComp = typeA.localeCompare(typeB);
      if (typeComp !== 0) return typeComp;
      return a.partyId.localeCompare(b.partyId);
    });
  };
  const filteredNotSubmitted = sortRowsByPartyType(notSubmitted.filter(matchSearch));
  const filteredSubmitted = sortRowsByPartyType(submitted.filter(matchSearch));
  const filteredApproved = sortRowsByPartyType(approved.filter(matchSearch));

  /* ===== ACTIONS ===== */
  const updateTimeSettings = async () => {
    setSavingSettings(true);
    try {
      const ref = doc(db, "settings", "cashierConfig");
      await setDoc(ref, { startTime, endTime }, { merge: true });
      alert("Cashier timing updated successfully!");
    } catch (err) {
      alert("Failed to update timing");
    }
    setSavingSettings(false);
  };

  const handleEdit = (row) => {
    setEditingRow(row.id);
    setEditDenoms(row.denominations || {});
    setEditRd(row.rdAmount || 0);
    setEditD(row.dAmount || 0);
  };

  const saveEdit = async (row) => {
    const totalCash = Object.entries(editDenoms).reduce((sum, [note, qty]) => sum + (Number(note) * Number(qty || 0)), 0);
    const newClosing = (Number(row.openingBalance) || 0) + 
                       (Number(row.orderAmount) || 0) + 
                       (Number(row.cdmAmount) || 0) + 
                       (Number(row.nicheBrandAmount) || 0) - 
                       (Number(row.onlineCollected) || 0) - 
                       totalCash +
                       (Number(editRd) || 0) -
                       (Number(editD) || 0);

    const ref = doc(db, "salesman_ledger", row.id);
    await updateDoc(ref, {
      denominations: editDenoms,
      cashCollected: totalCash,
      rdAmount: Number(editRd) || 0,
      dAmount: Number(editD) || 0,
      closingBalance: newClosing
    });

    await cascadeLedgerBalance(row.partyId, row.dateIST, newClosing);

    setSubmitted(prev => prev.map(p => p.id === row.id ? { 
      ...p, 
      denominations: editDenoms, 
      cashCollected: totalCash, 
      rdAmount: Number(editRd) || 0,
      dAmount: Number(editD) || 0,
      closingBalance: newClosing 
    } : p));
    setEditingRow(null);
  };

  const handleSyncLateSales = async (row) => {
    const pendingOrders = pendingOrdersMap[(row.partyId || "").trim()] || [];
    if (pendingOrders.length === 0) {
      alert("No unsynced orders found for this party.");
      return;
    }

    if (!window.confirm(`Found ${pendingOrders.length} unsynced orders for ${row.partyId}. Sync them to this ledger now?`)) {
      return;
    }

    try {
      let extraSalesAmount = 0;
      const newOrderIds = [];
      
      const pMode = partiesMap[row.partyId]?.payment_mode || "Cash";

      pendingOrders.forEach(order => {
        if (pMode === "Credit" || pMode === "Hawker") {
          if (order.approvedDateOnly < exportDate) {
            extraSalesAmount += Number(order.totalAmount || 0);
            newOrderIds.push(order.id);
          }
        } else {
          if (order.approvedDateOnly === exportDate) {
            extraSalesAmount += Number(order.totalAmount || 0);
            newOrderIds.push(order.id);
          }
        }
      });

      if (newOrderIds.length === 0) {
        alert("No unsynced orders found matching the date criteria.");
        return;
      }

      const updatedOrderAmount = (Number(row.orderAmount) || 0) + extraSalesAmount;
      const updatedConsumedIds = [...(row.consumedOrderIds || []), ...newOrderIds];
      const updatedClosing = (Number(row.closingBalance) || 0) + extraSalesAmount;

      const ref = doc(db, "salesman_ledger", row.id);
      await updateDoc(ref, {
        orderAmount: updatedOrderAmount,
        consumedOrderIds: updatedConsumedIds,
        closingBalance: updatedClosing
      });

      await updateOrderSyncStatus(newOrderIds, true);
      await cascadeLedgerBalance(row.partyId, row.dateIST, updatedClosing);

      setPendingOrdersMap(prev => {
        const copy = { ...prev };
        const pid = (row.partyId || "").trim();
        if (copy[pid]) {
          copy[pid] = copy[pid].filter(o => !newOrderIds.includes(o.id));
        }
        return copy;
      });

      const updateRowObj = (r) => {
        if (r.id === row.id) {
          return {
            ...r,
            orderAmount: updatedOrderAmount,
            consumedOrderIds: updatedConsumedIds,
            closingBalance: updatedClosing
          };
        }
        return r;
      };

      setSubmitted(prev => prev.map(updateRowObj));
      setApproved(prev => prev.map(updateRowObj));

      alert(`Successfully synced ${newOrderIds.length} orders totaling ₹${extraSalesAmount.toFixed(2)} to the ledger!`);
    } catch (err) {
      console.error("Error syncing late sales:", err);
      alert("Failed to sync late sales: " + err.message);
    }
  };

  const getGlobalUnsyncedStats = () => {
    let totalAmount = 0;
    let orderCount = 0;
    const targets = [];

    const allActiveRows = [...submitted, ...approved];

    allActiveRows.forEach(row => {
      const pendingOrders = pendingOrdersMap[(row.partyId || "").trim()] || [];
      if (pendingOrders.length === 0) return;

      const pMode = partiesMap[row.partyId]?.payment_mode || "Cash";
      let extraSalesAmount = 0;
      const newOrderIds = [];

      pendingOrders.forEach(order => {
        if (pMode === "Credit" || pMode === "Hawker") {
          if (order.approvedDateOnly < exportDate) {
            extraSalesAmount += Number(order.totalAmount || 0);
            newOrderIds.push(order.id);
          }
        } else {
          if (order.approvedDateOnly === exportDate) {
            extraSalesAmount += Number(order.totalAmount || 0);
            newOrderIds.push(order.id);
          }
        }
      });

      if (newOrderIds.length > 0) {
        totalAmount += extraSalesAmount;
        orderCount += newOrderIds.length;
        targets.push({
          row,
          newOrderIds,
          extraSalesAmount
        });
      }
    });

    return { totalAmount, orderCount, targets };
  };

  const handleSyncAllLateSales = async (targets) => {
    if (!targets || targets.length === 0) {
      alert("No unsynced orders to sync.");
      return;
    }

    const totalOrders = targets.reduce((sum, t) => sum + t.newOrderIds.length, 0);
    const totalSales = targets.reduce((sum, t) => sum + t.extraSalesAmount, 0);

    if (!window.confirm(`Found ${totalOrders} unsynced orders across ${targets.length} parties (Total: ₹${totalSales.toLocaleString("en-IN", { minimumFractionDigits: 2 })}). Sync all now?`)) {
      return;
    }

    try {
      const batch = writeBatch(db);

      targets.forEach(t => {
        const { row, newOrderIds, extraSalesAmount } = t;

        const updatedOrderAmount = (Number(row.orderAmount) || 0) + extraSalesAmount;
        const updatedConsumedIds = [...(row.consumedOrderIds || []), ...newOrderIds];
        const updatedClosing = (Number(row.closingBalance) || 0) + extraSalesAmount;

        const ledgerRef = doc(db, "salesman_ledger", row.id);
        batch.update(ledgerRef, {
          orderAmount: updatedOrderAmount,
          consumedOrderIds: updatedConsumedIds,
          closingBalance: updatedClosing
        });
        
        newOrderIds.forEach(id => {
          const orderRef = doc(db, "orders", id);
          batch.update(orderRef, { ledgerSynced: true });
        });
      });

      await batch.commit();

      await Promise.all(targets.map(async (t) => {
        const updatedClosing = (Number(t.row.closingBalance) || 0) + t.extraSalesAmount;
        await cascadeLedgerBalance(t.row.partyId, t.row.dateIST, updatedClosing);
      }));

      setPendingOrdersMap(prev => {
        const copy = { ...prev };
        targets.forEach(t => {
          const pid = (t.row.partyId || "").trim();
          if (copy[pid]) {
            copy[pid] = copy[pid].filter(o => !t.newOrderIds.includes(o.id));
          }
        });
        return copy;
      });

      const updateRowObj = (r) => {
        const target = targets.find(t => t.row.id === r.id);
        if (target) {
          return {
            ...r,
            orderAmount: (Number(r.orderAmount) || 0) + target.extraSalesAmount,
            consumedOrderIds: [...(r.consumedOrderIds || []), ...target.newOrderIds],
            closingBalance: (Number(r.closingBalance) || 0) + target.extraSalesAmount
          };
        }
        return r;
      };

      setSubmitted(prev => prev.map(updateRowObj));
      setApproved(prev => prev.map(updateRowObj));

      alert(`Successfully synced ${totalOrders} late bills across ${targets.length} parties!`);
    } catch (err) {
      console.error("Error batch syncing late sales:", err);
      alert("Failed to batch sync late sales: " + err.message);
    }
  };

  const approve = async (row) => {
    if (!window.confirm(`Approve settlement for ${row.partyId}?`)) return;
    const ref = doc(db, "salesman_ledger", row.id);
    await updateDoc(ref, {
      status: "APPROVED",
      dayClose: true,
      approvedAt: serverTimestamp(),
      approvedBy: auth.currentUser.email
    });
    // Local move
    setSubmitted(prev => prev.filter(p => p.id !== row.id));
    setApproved(prev => [...prev, { ...row, status: "APPROVED", dayClose: true }]);
  };

  const reject = async (row) => {
    const remark = prompt("Reason for rejection:");
    if (remark === null) return;

    try {
      // 1. Mark the ledger as REJECTED and RESET amounts
      const ref = doc(db, "salesman_ledger", row.id);
      
      // 🚩 RECALC: Closing = Opening + 0 (Rejected Sales) + CDM + Niche - Online - Cash
      const newClosing = (Number(row.openingBalance) || 0) + 
                         0 + // Order amount becomes 0 on rejection
                         (Number(row.cdmAmount) || 0) + 
                         (Number(row.nicheBrandAmount) || 0) - 
                         (Number(row.onlineCollected) || 0) - 
                         (Number(row.cashCollected) || 0);

      await updateDoc(ref, { 
        status: "REJECTED", 
        rejectionRemark: remark,
        orderAmount: 0,
        consumedOrderIds: [],
        closingBalance: newClosing,
        updatedAt: serverTimestamp()
      });

      await cascadeLedgerBalance(row.partyId, row.dateIST, newClosing);

      // 2. Un-sync the associated invoices (The Rubber Band Effect)
      if (row.consumedOrderIds && row.consumedOrderIds.length > 0) {
          console.log(`Rejection: Un-syncing ${row.consumedOrderIds.length} orders for ${row.id}...`);
          await updateOrderSyncStatus(row.consumedOrderIds, false);
      } else {
          console.warn("No consumedOrderIds found for this ledger. Rubber band effect might be limited for this specific record.");
      }

      // Update UI state
      setSubmitted(prev => prev.filter(p => p.id !== row.id));
      alert("Rejected successfully. The bills have been released for re-submission and the ledger amount reset to ₹0.00.");
    } catch (err) {
      console.error("Rejection Error:", err);
      alert("An error occurred during rejection: " + err.message);
    }
  };

  const exportExcel = async () => {
    // Combine all parties
    const allRows = [...filteredNotSubmitted, ...filteredSubmitted, ...filteredApproved];

    if (allRows.length === 0) {
      alert("No data for this date");
      return;
    }

    // Sort: CASH first, then Others
    const sortedRows = [...allRows].sort((a, b) => {
      const modeA = (partiesMap[a.partyId]?.payment_mode || "Cash").toUpperCase();
      const modeB = (partiesMap[b.partyId]?.payment_mode || "Cash").toUpperCase();

      if (modeA === "CASH" && modeB !== "CASH") return -1;
      if (modeA !== "CASH" && modeB === "CASH") return 1;
      
      // If same mode, secondary sort by party type
      const typeA = partiesMap[a.partyId]?.partytype || "";
      const typeB = partiesMap[b.partyId]?.partytype || "";
      const typeComp = typeA.localeCompare(typeB);
      if (typeComp !== 0) return typeComp;

      // Tertiary sort by partyId
      return a.partyId.localeCompare(b.partyId);
    });

    const getGroupKey = (pMode, pType) => {
      const mode = (pMode || "Cash").trim().toUpperCase();
      const type = (pType || "hawker").trim().toUpperCase();
      if (mode === "CASH" && type === "HAWKER") return "TOTAL(CASH)";
      if (mode === "CASH" && type === "SWD") return "TOTAL(SWD)";
      if (mode === "CASH" && type === "WHOLESALE") return "TOTAL(VAN SALES)";
      if (mode === "CREDIT" && type === "HAWKER") return "TOTAL (CREDIT)";
      return `TOTAL (${mode}-${type})`;
    };

    const resetSubtotal = (groupKey) => ({
      Date: "",
      Party: groupKey,
      "Payment Mode": "",
      "Party Type": "",
      Opening: 0,
      Sales: 0,
      CDM: 0,
      NicheBrands: 0,
      Online: 0,
      "₹500": 0,
      "₹200": 0,
      "₹100": 0,
      "₹50": 0,
      "₹20": 0,
      "₹10": 0,
      "₹5": 0,
      "₹1": 0,
      Cash: 0,
      RD: 0,
      D: 0,
      Closing: 0,
      Status: ""
    });

    const expNum = val => Number(Number(val || 0).toFixed(2));

    const excelRows = [];
    let currentGroup = null;
    let subtotal = null;

    sortedRows.forEach((p) => {
      const pMode = partiesMap[p.partyId]?.payment_mode || "Cash";
      const pType = partiesMap[p.partyId]?.partytype || "hawker";
      const liveCdm = Number(cdmMap[p.partyId]?.cdm || 0);
      const liveNiche = Number(cdmMap[p.partyId]?.niche || 0);
      const realOnline = Number(onlineCollectionMap[p.partyId] || 0);

      let orderAmount = Number(p.orderAmount) || 0;
      let closing = Number(p.closingBalance) || 0;
      let usedCdm = Number(p.cdmAmount) || 0;
      let usedNiche = Number(p.nicheBrandAmount) || 0;
      let usedOnline = Number(p.onlineCollected) || 0;

      // Apply live recalculation if NOT submitted
      if (p.status === "PENDING" || p.status === "REJECTED") {
        const livePendingOrders = pendingOrdersMap[(p.partyId || "").trim()] || [];
        let calc = 0;
        livePendingOrders.forEach(order => {
          if (pMode === "Credit" || pMode === "Hawker") {
            if (order.approvedDateOnly < exportDate) calc += Number(order.totalAmount || 0);
          } else {
            if (order.approvedDateOnly === exportDate) calc += Number(order.totalAmount || 0);
          }
        });
        orderAmount = calc;
        usedCdm = liveCdm;
        usedNiche = liveNiche;
        usedOnline = realOnline;
        closing = (Number(p.openingBalance) || 0) + orderAmount + usedCdm + usedNiche - usedOnline;
      }

      const groupKey = getGroupKey(pMode, pType);

      if (currentGroup && currentGroup !== groupKey) {
        if (subtotal) {
          const finalizedSubtotal = { ...subtotal };
          finalizedSubtotal.Opening = expNum(finalizedSubtotal.Opening);
          finalizedSubtotal.Sales = expNum(finalizedSubtotal.Sales);
          finalizedSubtotal.CDM = expNum(finalizedSubtotal.CDM);
          finalizedSubtotal.NicheBrands = expNum(finalizedSubtotal.NicheBrands);
          finalizedSubtotal.Online = expNum(finalizedSubtotal.Online);
          finalizedSubtotal.Cash = expNum(finalizedSubtotal.Cash);
          finalizedSubtotal.RD = expNum(finalizedSubtotal.RD);
          finalizedSubtotal.D = expNum(finalizedSubtotal.D);
          finalizedSubtotal.Closing = expNum(finalizedSubtotal.Closing);

          excelRows.push(finalizedSubtotal);
          excelRows.push({}); // blank spacer row
          excelRows.push({}); // double spacing to match original request layout
        }
        subtotal = null;
      }

      if (!subtotal) {
        subtotal = resetSubtotal(groupKey);
        currentGroup = groupKey;
      }

      const rowObj = {
        Date: p.dateIST,
        Party: p.partyId,
        "Payment Mode": pMode,
        "Party Type": pType,
        Opening: expNum(p.openingBalance),
        Sales: expNum(orderAmount),
        CDM: expNum(usedCdm),
        NicheBrands: expNum(usedNiche),
        Online: expNum(usedOnline),
        "₹500": Number(p.denominations?.[500] || 0),
        "₹200": Number(p.denominations?.[200] || 0),
        "₹100": Number(p.denominations?.[100] || 0),
        "₹50": Number(p.denominations?.[50] || 0),
        "₹20": Number(p.denominations?.[20] || 0),
        "₹10": Number(p.denominations?.[10] || 0),
        "₹5": Number(p.denominations?.[5] || 0),
        "₹1": Number(p.denominations?.[1] || 0),
        Cash: expNum(p.cashCollected),
        RD: expNum(p.rdAmount),
        D: expNum(p.dAmount),
        Closing: expNum(closing),
        Status: p.status || "PENDING"
      };

      excelRows.push(rowObj);

      // Accumulate
      subtotal.Opening += rowObj.Opening;
      subtotal.Sales += rowObj.Sales;
      subtotal.CDM += rowObj.CDM;
      subtotal.NicheBrands += rowObj.NicheBrands;
      subtotal.Online += rowObj.Online;
      subtotal["₹500"] += rowObj["₹500"];
      subtotal["₹200"] += rowObj["₹200"];
      subtotal["₹100"] += rowObj["₹100"];
      subtotal["₹50"] += rowObj["₹50"];
      subtotal["₹20"] += rowObj["₹20"];
      subtotal["₹10"] += rowObj["₹10"];
      subtotal["₹5"] += rowObj["₹5"];
      subtotal["₹1"] += rowObj["₹1"];
      subtotal.Cash += rowObj.Cash;
      subtotal.RD += rowObj.RD;
      subtotal.D += rowObj.D;
      subtotal.Closing += rowObj.Closing;
    });

    if (subtotal) {
      const finalizedSubtotal = { ...subtotal };
      finalizedSubtotal.Opening = expNum(finalizedSubtotal.Opening);
      finalizedSubtotal.Sales = expNum(finalizedSubtotal.Sales);
      finalizedSubtotal.CDM = expNum(finalizedSubtotal.CDM);
      finalizedSubtotal.NicheBrands = expNum(finalizedSubtotal.NicheBrands);
      finalizedSubtotal.Online = expNum(finalizedSubtotal.Online);
      finalizedSubtotal.Cash = expNum(finalizedSubtotal.Cash);
      finalizedSubtotal.RD = expNum(finalizedSubtotal.RD);
      finalizedSubtotal.D = expNum(finalizedSubtotal.D);
      finalizedSubtotal.Closing = expNum(finalizedSubtotal.Closing);
      excelRows.push(finalizedSubtotal);
    }

    const ws = XLSX.utils.json_to_sheet(excelRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "All Ledgers");
    XLSX.writeFile(wb, `Daily_Receivables_${exportDate}.xlsx`);
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center" }}><h3>Loading Ledger Data...</h3></div>;

  const { totalAmount: globalUnsyncedAmount, orderCount: globalUnsyncedCount, targets: globalUnsyncedTargets } = getGlobalUnsyncedStats();

  return (
    <div style={styles.container}>
      {/* Top Config Header */}
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center", 
        background: "#f8f9fa", 
        padding: "10px 20px", 
        borderRadius: 10,
        marginBottom: 20,
        border: "1px solid #dee2e6"
      }}>
        <div style={{ fontSize: 22, fontWeight: "bold", color: "#333" }}>Cashier Approval Portal</div>
        
        <div style={{ display: "flex", gap: 15, alignItems: "center" }}>
          <div style={{ fontSize: 14, color: "#666" }}>
            <b>Cashier Timing (IST):</b>
          </div>
          <input 
            type="time" 
            value={startTime} 
            onChange={(e) => setStartTime(e.target.value)}
            style={{ padding: 5, borderRadius: 4, border: "1px solid #ccc" }}
          />
          <span>to</span>
          <input 
            type="time" 
            value={endTime} 
            onChange={(e) => setEndTime(e.target.value)}
            style={{ padding: 5, borderRadius: 4, border: "1px solid #ccc" }}
          />
          <button 
            onClick={updateTimeSettings}
            disabled={savingSettings}
            style={{ 
              background: "#1976d2", 
              color: "#fff", 
              border: "none", 
              padding: "6px 15px", 
              borderRadius: 4, 
              cursor: "pointer",
              opacity: savingSettings ? 0.6 : 1
            }}
          >
            {savingSettings ? "Saving..." : "Update Timing"}
          </button>
        </div>
      </div>

      <div style={styles.tabRow}>
        <button style={styles.tabBtn(tab === "PENDING")} onClick={() => setTab("PENDING")}> Not Submitted </button>
        <button style={styles.tabBtn(tab === "SUBMITTED")} onClick={() => setTab("SUBMITTED")}> Submitted (Wait Approval) </button>
        <button style={styles.tabBtn(tab === "APPROVED")} onClick={() => setTab("APPROVED")}> Approved </button>
        <button style={{ ...styles.tabBtn(tab === "CASH_SUMMARY"), background: tab === "CASH_SUMMARY" ? "#673ab7" : "#eee" }} onClick={() => setTab("CASH_SUMMARY")}> 💰 Cash Summary </button>
      </div>

      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 20 }}>
        <input 
          type="date" 
          value={exportDate} 
          onChange={(e) => setExportDate(e.target.value)} 
          style={{ fontSize: 18, padding: 8, borderRadius: 6, border: "1px solid #ccc" }}
        />
        <input 
          type="text" 
          placeholder="🔍 Search Party by ID..." 
          value={searchTerm} 
          onChange={(e) => setSearchTerm(e.target.value)} 
          style={{ fontSize: 18, padding: 8, borderRadius: 6, border: "1px solid #1976d2", flex: 1, maxWidth: 300 }}
        />
        <button 
          onClick={exportExcel}
          disabled={exportCountdown > 0 || loading || globalUnsyncedCount > 0}
          style={{ 
            padding: "10px 20px", 
            fontSize: 16, 
            background: (exportCountdown > 0 || loading || globalUnsyncedCount > 0) ? "#adb5bd" : "#2e7d32", 
            color: "#fff", 
            border: "none", 
            borderRadius: 6, 
            cursor: (exportCountdown > 0 || loading || globalUnsyncedCount > 0) ? "not-allowed" : "pointer" 
          }}
        >
          {globalUnsyncedCount > 0 
            ? "⚠️ Sync Late Sales to Export" 
            : exportCountdown > 0 
            ? `Export XLSX (Wait ${exportCountdown}s)` 
            : "Export All Ledgers XLSX"}
        </button>
        {globalUnsyncedCount > 0 && (
          <button
            onClick={() => handleSyncAllLateSales(globalUnsyncedTargets)}
            className="global-sync-btn"
            style={{
              padding: "10px 24px",
              fontSize: 16,
              fontWeight: "900",
              background: "#ff8f00",
              color: "#fff",
              border: "3px solid #ff6f00",
              borderRadius: 8,
              cursor: "pointer",
              boxShadow: "0 4px 15px rgba(255, 143, 0, 0.5)",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              textTransform: "uppercase"
            }}
          >
            ⚠️ Sync All Late Sales ({globalUnsyncedCount} Bills - ₹{globalUnsyncedAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })})
          </button>
        )}
      </div>

      {exportDate < todayIST && userRole !== "admin" && userRole !== "superadmin" && (
        <div style={{ background: "#fff9c4", color: "#f57f17", padding: "12px 20px", borderRadius: 8, marginBottom: 20, border: "1px solid #fbc02d", fontWeight: "bold" }}>
          ⚠️ You are viewing past ledger entries. Modifying or approving past records is restricted to Admins.
        </div>
      )}

      {/* 1. NOT SUBMITTED / REJECTED */}
      {tab === "PENDING" && (
        <div className="approval-table-wrapper">
          <h3 style={{ marginBottom: 15, color: "#666" }}>Parties who haven't visited or were REJECTED</h3>
          {filteredNotSubmitted.length === 0 ? <p>No matching parties! 🎉</p> : (
            <table className="approval-table">
              <thead>
                <tr>
                   <th>Party</th>
                  <th>Mode</th>
                  <th>Party Type</th>
                  <th>Opening</th>
                  <th>Sales (+)</th>
                  <th>CDM (+)</th>
                  <th>Niche (+)</th>
                  <th>Online (-)</th>
                  <th>Est. Closing</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredNotSubmitted.map(p => {
                  const realOnline = Number(onlineCollectionMap[p.partyId] || 0);
                  const liveCdm = Number(cdmMap[p.partyId]?.cdm || 0);
                  const liveNiche = Number(cdmMap[p.partyId]?.niche || 0);

                  const pMode = partiesMap[p.partyId]?.payment_mode || "Cash";
                  const livePendingOrders = pendingOrdersMap[(p.partyId || "").trim()] || [];
                  let liveOrderAmount = Number(p.orderAmount) || 0;

                  // 🚩 Determine real-time debt for pending/rejected parties
                  if (p.status === "PENDING" || p.status === "REJECTED") {
                    let calc = 0;
                    livePendingOrders.forEach(order => {
                      if (pMode === "Credit" || pMode === "Hawker") {
                        if (order.approvedDateOnly < exportDate) {
                          calc += Number(order.totalAmount || 0);
                        }
                      } else {
                        if (order.approvedDateOnly === exportDate) {
                          calc += Number(order.totalAmount || 0);
                        }
                      }
                    });
                    liveOrderAmount = calc; 
                  }

                  const estClosing = (Number(p.openingBalance) || 0) + 
                                   liveOrderAmount + 
                                   liveCdm + 
                                   liveNiche - 
                                   realOnline;
                  
                  const isToday = exportDate === todayIST;

                  return (
                    <tr key={p.id}>
                      <td><b>{p.partyId}</b></td>
                      <td>{pMode}</td>
                      <td style={{ textTransform: "capitalize" }}>{partiesMap[p.partyId]?.partytype || "hawker"}</td>
                      <td>₹ {fmt(p.openingBalance)}</td>
                      <td>₹ {fmt(liveOrderAmount)}</td>
                      <td style={{ color: '#1976d2' }}>₹ {fmt(liveCdm)}</td>
                      <td style={{ color: '#17a2b8' }}>₹ {fmt(liveNiche)}</td>
                      <td>₹ {fmt(realOnline)}</td>
                      <td style={{ fontWeight: 'bold', color: '#f74605' }}>₹ {fmt(estClosing)}</td>
                      <td>
                        <button
                          disabled={!isToday}
                          onClick={() => navigate(`/cashier?partyId=${p.partyId}`)}
                          style={{
                            ...styles.actionBtn,
                            background: isToday ? "#1976d2" : "#adb5bd",
                            color: "#fff",
                            cursor: isToday ? "pointer" : "not-allowed"
                          }}
                        >
                          Submit Cash ➔
                        </button>
                      </td>
                    </tr>
                  );
                })}

              </tbody>
            </table>
          )}
        </div>
      )}

      {/* 2. SUBMITTED (WAITING APPROVAL) */}
      {tab === "SUBMITTED" && (
        <div className="approval-table-wrapper">
          <h3 style={{ marginBottom: 15, color: "#f57c00" }}>Waiting for Cashier Verification (Active Queue)</h3>
          {filteredSubmitted.length === 0 ? <p>No entries wait approval</p> : (
            <table className="approval-table">
              <thead>
                <tr>
                   <th>Party</th>
                  <th>Status</th>
                  <th>Opening</th>
                  <th>Sales</th>
                  <th>CDM</th>
                  <th>Niche</th>
                  <th>Online</th>
                  <th>Cash Collected</th>
                  <th>RD (+)</th>
                  <th>D (-)</th>
                  <th>Closing</th>
                  <th>Denominations (Edit Mode Enabled)</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSubmitted.map(p => {
                  const unsyncedOrders = pendingOrdersMap[(p.partyId || "").trim()] || [];
                  const pMode = partiesMap[p.partyId]?.payment_mode || "Cash";
                  let lateSalesAmount = 0;
                  const lateOrderIds = [];
                  unsyncedOrders.forEach(order => {
                    if (pMode === "Credit" || pMode === "Hawker") {
                      if (order.approvedDateOnly < exportDate) {
                        lateSalesAmount += Number(order.totalAmount || 0);
                        lateOrderIds.push(order.id);
                      }
                    } else {
                      if (order.approvedDateOnly === exportDate) {
                        lateSalesAmount += Number(order.totalAmount || 0);
                        lateOrderIds.push(order.id);
                      }
                    }
                  });

                  return (
                    <tr key={p.id} className="pending-row">
                      <td>
                        <b>{p.partyId}</b><br/>
                        <small>{partiesMap[p.partyId]?.payment_mode} / {partiesMap[p.partyId]?.partytype}</small>
                        {lateOrderIds.length > 0 && (
                          <div style={{ 
                            marginTop: 10, 
                            background: "#fff8e1", 
                            border: "2px solid #ffb300", 
                            borderRadius: 8, 
                            padding: "8px 12px", 
                            display: "inline-flex", 
                            alignItems: "center", 
                            gap: 12,
                            boxShadow: "0 2px 8px rgba(255, 179, 0, 0.15)"
                          }}>
                            <span style={{ 
                              color: "#b78103", 
                              fontSize: 14, 
                              fontWeight: "800" 
                            }}>
                              ⚠️ Unsynced Bills: ₹{lateSalesAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                            </span>
                            <button
                              onClick={() => handleSyncLateSales(p)}
                              style={{
                                background: "#ff6f00", 
                                color: "#fff", 
                                border: "none", 
                                padding: "6px 14px", 
                                borderRadius: 6, 
                                fontSize: 12, 
                                fontWeight: "900", 
                                cursor: "pointer",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
                                textTransform: "uppercase"
                              }}
                            >
                              Sync Now ➔
                            </button>
                          </div>
                        )}
                      </td>
                    <td>
                      <span style={{ 
                        background: p.status === "REJECTED" ? "#dc3545" : "#f74605", 
                        color: "#fff", padding: "4px 8px", borderRadius: 4, fontWeight: "bold", fontSize: 12 
                      }}>
                        {p.status === "REJECTED" ? "REJECTED" : "SUBMITTED"}
                      </span>
                    </td>
                     <td>₹ {fmt(p.openingBalance)}</td>
                    <td>₹ {fmt(p.orderAmount)}</td>
                    <td style={{ color: '#1976d2' }}>₹ {fmt(p.cdmAmount)}</td>
                    <td style={{ color: '#17a2b8' }}>₹ {fmt(p.nicheBrandAmount)}</td>
                    <td>₹ {fmt(p.onlineCollected)}</td>
                    <td style={{ fontWeight: 'bold' }}>₹ {fmt(p.cashCollected)}</td>
                    <td>
                      {editingRow === p.id ? (
                        <input
                          type="number"
                          style={{ width: 85, padding: 6, fontSize: 20, fontWeight: 'bold', border: '2px solid #1976d2' }}
                          value={editRd}
                          onChange={(e) => setEditRd(e.target.value)}
                        />
                      ) : (
                        <span>₹ {fmt(p.rdAmount || 0)}</span>
                      )}
                    </td>
                    <td>
                      {editingRow === p.id ? (
                        <input
                          type="number"
                          style={{ width: 85, padding: 6, fontSize: 20, fontWeight: 'bold', border: '2px solid #1976d2' }}
                          value={editD}
                          onChange={(e) => setEditD(e.target.value)}
                        />
                      ) : (
                        <span>₹ {fmt(p.dAmount || 0)}</span>
                      )}
                    </td>
                    <td style={{ color: '#f32103' }}>₹ {fmt(p.closingBalance)}</td>
                    <td style={{ fontSize: 24, fontWeight: '900', color: '#333', minWidth: 250 }}>
                      {editingRow === p.id ? (
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                              {DENOM_ORDER.map(note => (
                                  <div key={note} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                      <span style={{ fontSize: 18, minWidth: 50, fontWeight: '900' }}>₹{note}:</span>
                                      <input 
                                        type="number" 
                                        style={{ width: 80, padding: 8, fontSize: 22, fontWeight: 'bold', border: '2px solid #1976d2' }} 
                                        value={editDenoms[note] || ""} 
                                        onChange={(e) => setEditDenoms({ ...editDenoms, [note]: e.target.value })}
                                      />
                                  </div>
                              ))}
                          </div>
                      ) : (
                        Object.entries(p.denominations || {})
                          .filter(([_, qty]) => Number(qty) > 0)
                          .sort((a, b) => Number(b[0]) - Number(a[0])) // Descending
                          .map(([note, qty]) => `${note}x${qty}`)
                          .join(", ") || "No cash"
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexDirection: 'column' }}>
                        {(exportDate < todayIST && userRole !== "admin" && userRole !== "superadmin") ? (
                            <span style={{ color: "#d32f2f", fontSize: "12px", fontWeight: "bold", textAlign: "center", padding: "4px" }}>Admin Required<br/>for Past Dates</span>
                        ) : (
                            <>
                                {editingRow === p.id ? (
                                    <button style={{ ...styles.actionBtn, background: '#1976d2', color: '#fff' }} onClick={() => saveEdit(p)}>💾 Save Info</button>
                                ) : (
                                    <button style={{ ...styles.actionBtn, background: '#666', color: '#fff' }} onClick={() => handleEdit(p)}>✏️ Edit Cash</button>
                                )}
                                <button style={{ ...styles.actionBtn, background: '#2e7d32', color: '#fff' }} onClick={() => approve(p)}>✓ Approve</button>
                                <button style={{ ...styles.actionBtn, background: '#c62828', color: '#fff' }} onClick={() => reject(p)}>✕ Reject</button>
                            </>
                        )}
                      </div>
                    </td>
                  </tr>
                )})}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* 3. APPROVED (HISTORY) */}
      {tab === "APPROVED" && (
        <div className="approval-table-wrapper">
          <h3 style={{ marginBottom: 15, color: "#2e7d32" }}>Finalized settlements for {exportDate}</h3>
          {filteredApproved.length === 0 ? <p>No matching records</p> : (
            <table className="approval-table">
              <thead>
                <tr>
                   <th>Party</th>
                  <th>Opening</th>
                  <th>Sales</th>
                  <th>CDM</th>
                  <th>Niche</th>
                  <th>Online</th>
                  <th>Cash</th>
                  <th>RD (+)</th>
                  <th>D (-)</th>
                  <th>Closing</th>
                </tr>
              </thead>
              <tbody>
                {filteredApproved.map(p => {
                  const unsyncedOrders = pendingOrdersMap[(p.partyId || "").trim()] || [];
                  const pMode = partiesMap[p.partyId]?.payment_mode || "Cash";
                  let lateSalesAmount = 0;
                  const lateOrderIds = [];
                  unsyncedOrders.forEach(order => {
                    if (pMode === "Credit" || pMode === "Hawker") {
                      if (order.approvedDateOnly < exportDate) {
                        lateSalesAmount += Number(order.totalAmount || 0);
                        lateOrderIds.push(order.id);
                      }
                    } else {
                      if (order.approvedDateOnly === exportDate) {
                        lateSalesAmount += Number(order.totalAmount || 0);
                        lateOrderIds.push(order.id);
                      }
                    }
                  });

                  return (
                    <tr key={p.id} className="approved-row">
                      <td>
                        <b>{p.partyId}</b><br/>
                        <small>{partiesMap[p.partyId]?.payment_mode} / {partiesMap[p.partyId]?.partytype}</small>
                        {lateOrderIds.length > 0 && (
                          <div style={{ 
                            marginTop: 10, 
                            background: "#fff8e1", 
                            border: "2px solid #ffb300", 
                            borderRadius: 8, 
                            padding: "8px 12px", 
                            display: "inline-flex", 
                            alignItems: "center", 
                            gap: 12,
                            boxShadow: "0 2px 8px rgba(255, 179, 0, 0.15)"
                          }}>
                            <span style={{ 
                              color: "#b78103", 
                              fontSize: 14, 
                              fontWeight: "800" 
                            }}>
                              ⚠️ Unsynced Bills: ₹{lateSalesAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                            </span>
                            <button
                              onClick={() => handleSyncLateSales(p)}
                              style={{
                                background: "#ff6f00", 
                                color: "#fff", 
                                border: "none", 
                                padding: "6px 14px", 
                                borderRadius: 6, 
                                fontSize: 12, 
                                fontWeight: "900", 
                                cursor: "pointer",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
                                textTransform: "uppercase"
                              }}
                            >
                              Sync Now ➔
                            </button>
                          </div>
                        )}
                      </td>
                      <td>₹ {fmt(p.openingBalance)}</td>
                      <td>₹ {fmt(p.orderAmount)}</td>
                      <td style={{ color: '#1976d2' }}>₹ {fmt(p.cdmAmount)}</td>
                      <td style={{ color: '#17a2b8' }}>₹ {fmt(p.nicheBrandAmount)}</td>
                      <td>₹ {fmt(p.onlineCollected)}</td>
                      <td>₹ {fmt(p.cashCollected)}</td>
                      <td>₹ {fmt(p.rdAmount || 0)}</td>
                      <td>₹ {fmt(p.dAmount || 0)}</td>
                      <td style={{ fontWeight: 'bold', color: '#f32103' }}>₹ {fmt(p.closingBalance)}</td>
                    </tr>
                  )})}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* 4. CASH SUMMARY TAB */}
      {tab === "CASH_SUMMARY" && (
          <div className="approval-table-wrapper" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <h2 style={{ color: '#673ab7', marginBottom: 20 }}>Today's Total Cash Aggregation (Approved Only)</h2>
              <div style={{ 
                  background: '#fff', 
                  padding: 30, 
                  borderRadius: 15, 
                  boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
                  minWidth: 400
              }}>
                  {DENOM_ORDER.map(note => {
                      const totalQty = approved.reduce((sum, row) => sum + (Number(row.denominations?.[note]) || 0), 0);
                      if (totalQty === 0) return null;
                      return (
                          <div key={note} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 24, padding: '12px 0', borderBottom: '1px solid #eee' }}>
                              <span><b>{note}</b> X {totalQty}</span>
                              <span style={{ color: '#1976d2', fontWeight: 'bold' }}>= ₹ {fmt(note * totalQty)}</span>
                          </div>
                      );
                  })}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 36, fontWeight: 'bold', marginTop: 20, color: '#2e7d32' }}>
                      <span>Total Cash:</span>
                      <span>₹ {fmt(approved.reduce((sum, r) => sum + (Number(r.cashCollected) || 0), 0))}</span>
                  </div>
              </div>
              <p style={{ marginTop: 20, color: '#888' }}>* This summary aggregates all denominations from APPROVED ledgers for {exportDate}.</p>
          </div>
      )}
      <style>{`
        .approval-table-wrapper { margin-top: 20px; overflow-x: auto; background: #fff; padding: 20px; border-radius: 12px; box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
        .approval-table { width: 100%; border-collapse: collapse; min-width: 1200px; }
        .approval-table th { text-align: left; padding: 12px; border-bottom: 2px solid #eee; color: #666; font-size: 18px; }
        .approval-table td { padding: 18px 12px; border-bottom: 1px solid #f5f5f5; font-size: 24px; vertical-align: middle; font-weight: 700; }
        .approved-row { background-color: #f1f8e9; }
        .pending-row { background-color: #fffde7; }
        .global-sync-btn {
          animation: pulse 1.5s infinite alternate;
          transition: transform 0.2s ease, background-color 0.2s ease;
        }
        .global-sync-btn:hover {
          background-color: #ff6f00 !important;
          border-color: #e65100 !important;
          transform: scale(1.05);
        }
        .global-sync-btn:active {
          transform: scale(0.98);
        }
        @keyframes pulse {
          0% {
            box-shadow: 0 4px 10px rgba(255, 143, 0, 0.4);
          }
          100% {
            box-shadow: 0 4px 25px rgba(255, 143, 0, 0.85);
          }
        }
      `}</style>
    </div>
  );
}
